/**
 * Privacy — plain-English privacy notice in the Residence marketing language.
 */
import {
  MarketingLayout, Eyebrow, Display, SectionTitle, FocalSentence,
  Body, Section, Hairline, SecondaryLink,
} from "./MarketingLayout";

function Block({ title, children, testid }) {
  return (
    <div className="max-w-[62ch]" data-testid={testid}>
      <SectionTitle>{title}</SectionTitle>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

export default function PrivacyPage() {
  return (
    <MarketingLayout testid="privacy-page">
      <Section testid="privacy-hero" className="pt-24 sm:pt-32 pb-14">
        <div className="max-w-[820px]">
          <Eyebrow>Privacy</Eyebrow>
          <Display className="mt-6">Your household is yours.</Display>
          <FocalSentence className="mt-8 max-w-[46ch]">
            ARA holds the everyday details of family life. That is a responsibility we take personally.
          </FocalSentence>
          <Body className="mt-6" style={{ color: 'var(--ara-ink-soft, #7A7772)' }}>
            Last updated June 2026 · Written in plain English, on purpose.
          </Body>
        </div>
      </Section>

      <Hairline />

      <Section testid="privacy-body" className="py-16 space-y-14">
        <Block title="What ARA collects" testid="privacy-what-we-collect">
          <Body>
            The information you choose to put into your Residence: your name and email
            address, the household you create, the family members you add, and the
            everyday things you record — bills, goals, meals, chores, documents and notes.
          </Body>
          <Body>
            If you connect a bank, ARA also receives your account names, balances and
            transactions so The Study can stay up to date.
          </Body>
        </Block>

        <Block title="Why we collect it" testid="privacy-why">
          <Body>
            For one reason: so ARA can quietly reduce the mental load of running your
            household. Nothing is collected for advertising, profiling or resale.
          </Body>
        </Block>

        <Block title="Banking information" testid="privacy-banking">
          <Body>
            Bank connections are made through Akahu, a New Zealand open-banking provider.
            ARA never sees or stores your internet banking password. The connection is
            read-only: ARA can see account and transaction information, and cannot move
            money, make payments or change anything at your bank.
          </Body>
          <Body>
            The credentials that keep the connection alive are held on our servers only.
            They are never sent to your browser or included in the pages you load.
          </Body>
        </Block>

        <Block title="Household and family information" testid="privacy-household">
          <Body>
            Family member profiles, routines, moods and notes belong to your household and
            are visible only within it. Children's profiles exist only because a parent or
            caregiver created them, and they are treated with the same care as everything else.
          </Body>
        </Block>

        <Block title="What we do not do" testid="privacy-what-we-dont">
          <Body>We do not sell your information. Not to advertisers, not to data brokers, not to anyone.</Body>
          <Body>We do not show you advertising.</Body>
          <Body>We do not use your family's information to train public AI models.</Body>
        </Block>

        <Block title="Data retention" testid="privacy-retention">
          <Body>
            Your information stays in your Residence for as long as you keep your account.
            If you disconnect a bank, syncing stops immediately; the transactions already in
            your Study remain until you ask us to remove them.
          </Body>
        </Block>

        <Block title="Deleting your information" testid="privacy-deletion">
          <Body>
            You can ask us to delete your account and everything in it at any time through
            the <SecondaryLink to="/contact">Contact page</SecondaryLink>. We will confirm
            when it is done. Deletion is permanent.
          </Body>
        </Block>

        <Block title="Questions" testid="privacy-contact">
          <Body>
            If anything here is unclear, or you want to know exactly what ARA holds about
            your household, ask us — <SecondaryLink to="/contact">we answer personally</SecondaryLink>.
          </Body>
        </Block>
      </Section>
    </MarketingLayout>
  );
}
